/**
 * avatars/avatar-builder/builder-torso-arms.js
 * Xây dựng thân mình (Torso) và cánh tay (Arms) dạng Classic hoặc Articulated cho BlockyAvatar
 */

import * as THREE from 'three';
import { generateProceduralTexture } from '../avatar-presets.js';

export function buildTorso(avatar, pelvis, dimensions, materials) {
  const { torsoH, torsoW, torsoD, bodyType } = dimensions;
  const { skinMat, pantsMat, shoesMat, sleeveMat } = materials;

  const torsoGroup = new THREE.Group();
  torsoGroup.position.set(0, 0, 0);
  pelvis.add(torsoGroup);
  avatar.parts.torso = torsoGroup;

  if (bodyType === 'elder') {
    torsoGroup.rotation.x = 0.12; // Lưng người già hơi gù
  }

  // Mặt trước thân: Canvas Texture
  const torsoCanvas = generateProceduralTexture('torso', {
    shirtColor: avatar.config.colors.shirt,
    skinColor: avatar.config.colors.skin,
    outfit: avatar.config.features.outfit,
    isLabCoat: avatar.config.features.isLabCoat
  });
  avatar.torsoCanvas = torsoCanvas;
  const torsoTex = new THREE.CanvasTexture(torsoCanvas);
  torsoTex.colorSpace = THREE.SRGBColorSpace;
  avatar.torsoTex = torsoTex;
  avatar.disposables.push(torsoTex);

  // Mặt sau lưng: Canvas Texture (hỗ trợ khoét nách và bả vai cho áo ba lỗ)
  const torsoBackCanvas = generateProceduralTexture('torso_back', {
    shirtColor: avatar.config.colors.shirt,
    skinColor: avatar.config.colors.skin,
    outfit: avatar.config.features.outfit
  });
  avatar.torsoBackCanvas = torsoBackCanvas;
  const torsoBackTex = new THREE.CanvasTexture(torsoBackCanvas);
  torsoBackTex.colorSpace = THREE.SRGBColorSpace;
  avatar.torsoBackTex = torsoBackTex;
  avatar.disposables.push(torsoBackTex);

  // Màu vải áo thân mình cho 2 bên hông và vai: LUÔN DÙNG MÀU ÁO (shirtColor), không lấy nhầm màu da của tay áo!
  const bodyShirtColor = avatar.config.features.isLabCoat ? '#f8fafc' : avatar.config.colors.shirt;
  const torsoSideMat = new THREE.MeshStandardMaterial({ color: bodyShirtColor, roughness: 0.7 });
  const torsoFrontMat = new THREE.MeshStandardMaterial({ map: torsoTex, roughness: 0.7 });
  const torsoBackMat = new THREE.MeshStandardMaterial({ map: torsoBackTex, roughness: 0.7 });
  avatar.disposables.push(torsoSideMat, torsoFrontMat, torsoBackMat);

  const torsoMaterials = [
    torsoSideMat, // 0: +X (hông phải)
    torsoSideMat, // 1: -X (hông trái)
    torsoSideMat, // 2: +Y (đỉnh vai)
    pantsMat, // 3: -Y (đáy nối quần)
    torsoFrontMat, // 4: +Z (mặt trước ngực/bụng)
    torsoBackMat // 5: -Z (sau lưng)
  ];

  const torsoGeom = new THREE.BoxGeometry(torsoW, torsoH, torsoD);
  avatar.disposables.push(torsoGeom);
  const torsoMesh = new THREE.Mesh(torsoGeom, torsoMaterials);
  torsoMesh.position.y = torsoH / 2;
  torsoMesh.castShadow = false;
  torsoMesh.receiveShadow = true;
  torsoGroup.add(torsoMesh);
  avatar.torsoMesh = torsoMesh;

  avatar.materials = {
    skinMat,
    pantsMat,
    shoesMat,
    sleeveMat,
    torsoSideMat,
    torsoFrontMat,
    torsoBackMat
  };

  // Chi tiết 3D theo trang phục (tà áo dài, vạt blouse, mũ hoodie, tạp dề...)
  avatar.flaps = [];
  avatar.attachOutfitExtras(avatar.config.features.outfit);

  return torsoGroup;
}

export function buildArms(avatar, torsoGroup, dimensions, materials) {
  const { torsoH, torsoW, armH, armW, armD } = dimensions;
  const { skinMat, sleeveMat, bareArmMat, handSkinMat, isBareArm } = materials;

  const isShortSleeve = !!avatar.config.features.shortSleeve;
  const sleeveH = isShortSleeve ? armH * 0.38 : armH * 0.74;
  const handH = armH - sleeveH;

  const buildClassicArm = (isLeft) => {
    const armGroup = new THREE.Group();
    armGroup.name = isLeft ? 'LeftArm_Classic' : 'RightArm_Classic';
    const posX = isLeft ? -torsoW / 2 - armW / 2 : torsoW / 2 + armW / 2;
    armGroup.position.set(posX, torsoH, 0);

    // Ống tay áo
    const sleeveGeo = new THREE.BoxGeometry(armW, sleeveH, armD);
    const sleeveMesh = new THREE.Mesh(sleeveGeo, sleeveMat);
    sleeveMesh.position.y = -sleeveH / 2;
    sleeveMesh.castShadow = false;
    sleeveMesh.receiveShadow = true;
    armGroup.add(sleeveMesh);

    // Cẳng tay & Bàn tay: phẳng phiu, không cổ tay phồng lồi, dùng bareArmMat nếu là áo ba lỗ
    const handGeo = new THREE.BoxGeometry(armW, handH, armD);
    const handMesh = new THREE.Mesh(handGeo, isBareArm ? bareArmMat : handSkinMat);
    handMesh.position.y = -sleeveH - handH / 2;
    handMesh.castShadow = false;
    handMesh.receiveShadow = true;
    armGroup.add(handMesh);

    avatar.disposables.push(sleeveGeo, handGeo);
    return armGroup;
  };

  const buildArticulatedArm = (isLeft) => {
    const armGroup = new THREE.Group();
    armGroup.name = isLeft ? 'LeftUpperArm' : 'RightUpperArm';
    const posX = isLeft ? -torsoW / 2 - armW / 2 : torsoW / 2 + armW / 2;
    armGroup.position.set(posX, torsoH, 0);

    const upperArmH = armH * 0.5;
    const forearmH = armH - upperArmH;

    // 1. Upper Arm (Bắp tay trên)
    const upperSleeveH = isShortSleeve ? upperArmH * 0.72 : upperArmH;
    const upperArmSleeveGeo = new THREE.BoxGeometry(armW, upperSleeveH, armD);
    const upperArmSleeveMesh = new THREE.Mesh(upperArmSleeveGeo, sleeveMat);
    upperArmSleeveMesh.position.y = -upperSleeveH / 2;
    upperArmSleeveMesh.castShadow = false;
    upperArmSleeveMesh.receiveShadow = true;
    armGroup.add(upperArmSleeveMesh);

    if (isShortSleeve) {
      const upperSkinH = upperArmH - upperSleeveH;
      const upperSkinGeo = new THREE.BoxGeometry(armW, upperSkinH, armD);
      const upperSkinMesh = new THREE.Mesh(upperSkinGeo, isBareArm ? bareArmMat : skinMat);
      upperSkinMesh.position.y = -upperSleeveH - upperSkinH / 2;
      upperSkinMesh.castShadow = false;
      upperSkinMesh.receiveShadow = true;
      armGroup.add(upperSkinMesh);
      avatar.disposables.push(upperSkinGeo);
    }

    // 2. Forearm Group (Cẳng tay & Bàn tay xoay quanh khuỷu tay)
    const forearmGroup = new THREE.Group();
    forearmGroup.name = isLeft ? 'LeftForearm' : 'RightForearm';
    forearmGroup.position.set(0, -upperArmH, 0); // Khuỷu tay
    armGroup.add(forearmGroup);

    // KHỚP TRỤ BÁN NGUYỆT KHUỶU TAY (Cylindrical Elbow Hinge Plug)
    const elbowR = armD * 0.45;
    const elbowWidth = armW * 0.92;
    const elbowPlugGeo = new THREE.CylinderGeometry(elbowR, elbowR, elbowWidth, 24);
    elbowPlugGeo.rotateZ(Math.PI / 2);
    const elbowPlugMat = isShortSleeve ? (isBareArm ? bareArmMat : skinMat) : sleeveMat;
    const elbowPlugMesh = new THREE.Mesh(elbowPlugGeo, elbowPlugMat);
    elbowPlugMesh.position.set(0, 0, 0);
    elbowPlugMesh.castShadow = false;
    elbowPlugMesh.receiveShadow = true;
    forearmGroup.add(elbowPlugMesh);

    if (isShortSleeve) {
      const lowerArmGeo = new THREE.BoxGeometry(armW, forearmH, armD);
      const lowerArmMesh = new THREE.Mesh(lowerArmGeo, isBareArm ? bareArmMat : handSkinMat);
      lowerArmMesh.position.y = -forearmH / 2;
      lowerArmMesh.castShadow = false;
      lowerArmMesh.receiveShadow = true;
      forearmGroup.add(lowerArmMesh);
      avatar.disposables.push(lowerArmGeo);
    } else {
      const lowerSleeveH = forearmH * 0.48;
      const handSegH = forearmH - lowerSleeveH;

      const lowerSleeveGeo = new THREE.BoxGeometry(armW, lowerSleeveH, armD);
      const lowerSleeveMesh = new THREE.Mesh(lowerSleeveGeo, sleeveMat);
      lowerSleeveMesh.position.y = -lowerSleeveH / 2;
      lowerSleeveMesh.castShadow = false;
      lowerSleeveMesh.receiveShadow = true;
      forearmGroup.add(lowerSleeveMesh);

      const handGeo = new THREE.BoxGeometry(armW, handSegH, armD);
      const handMesh = new THREE.Mesh(handGeo, handSkinMat);
      handMesh.position.y = -lowerSleeveH - handSegH / 2;
      handMesh.castShadow = false;
      handMesh.receiveShadow = true;
      forearmGroup.add(handMesh);
      avatar.disposables.push(lowerSleeveGeo, handGeo);
    }

    // Điểm cầm đồ vật ở đầu bàn tay
    const handSlot = new THREE.Group();
    handSlot.name = isLeft ? 'LeftHandSlot' : 'RightHandSlot';
    handSlot.position.set(0, -forearmH, 0);
    forearmGroup.add(handSlot);

    avatar.disposables.push(upperArmSleeveGeo, elbowPlugGeo);
    return { armGroup, forearmGroup, handSlot };
  };

  if (avatar.isArticulated) {
    const leftArmData = buildArticulatedArm(true);
    torsoGroup.add(leftArmData.armGroup);
    avatar.parts.leftArm = leftArmData.armGroup;
    avatar.parts.leftUpperArm = leftArmData.armGroup;
    avatar.parts.leftForearm = leftArmData.forearmGroup;
    avatar.parts.leftHand = leftArmData.handSlot;

    const rightArmData = buildArticulatedArm(false);
    torsoGroup.add(rightArmData.armGroup);
    avatar.parts.rightArm = rightArmData.armGroup;
    avatar.parts.rightUpperArm = rightArmData.armGroup;
    avatar.parts.rightForearm = rightArmData.forearmGroup;
    avatar.parts.rightHand = rightArmData.handSlot;
  } else {
    const leftArmGroup = buildClassicArm(true);
    torsoGroup.add(leftArmGroup);
    avatar.parts.leftArm = leftArmGroup;

    const rightArmGroup = buildClassicArm(false);
    torsoGroup.add(rightArmGroup);
    avatar.parts.rightArm = rightArmGroup;
  }
}
